import type { Input } from '../../src/engine/schema.js';

/**
 * Every number the tool displays enters here, once, with a source and a date.
 *
 * `scenarioValues` is where scenarios do their work: change a fact about the
 * world in one place and every metric that depends on it recomputes.
 */
export const inputs: Input[] = [
  // ================================================================ economic

  {
    id: 'gross_new_taxes_28yr',
    label: 'Gross projected new property taxes over the agreement term',
    plainLabel: 'total new property taxes',
    unit: 'usd',
    value: 167_500_000,
    scenarioValues: { not_built: 0 },
    kind: 'fact',
    sigFigs: 4,
    asOf: '2026-01-20',
    sourceIds: [
      'src_city_subsidy',
      'src_goodhue_rates',
      'src_mn_state_general',
      'src_stat_272_02',
    ],
    confidence: 'documented',
    note:
      'The city’s own projection, before the abatement is netted out. It is land and building only — Minnesota does not tax business equipment, so the servers are outside this number entirely. The combined local rate on a commercial parcel here is 148.157875% of net tax capacity, plus the state general tax at 28.313%.',
  },
  {
    id: 'abatement_total_28yr',
    label: 'Property tax abatement granted',
    plainLabel: 'taxes given back to the developer',
    unit: 'usd',
    value: 36_578_343,
    scenarioValues: { not_built: 0 },
    kind: 'fact',
    sigFigs: 4,
    asOf: '2026-02-04',
    sourceIds: ['src_city_subsidy', 'src_stat_469_1813'],
    confidence: 'documented',
    note:
      'Approved 4 February 2026. Minnesota abatement is a pay-and-rebate: full tax is levied and collected, then returned. That makes it visible on the books, unlike tax increment financing.',
    contested: [
      {
        claim: 'The city describes the abatement as running up to 28 years.',
        counterClaim:
          'Minn. Stat. § 469.1813 caps a standard abatement at 15 years, and 20 only for utility property valued under Minn. R. ch. 8100. Either this is structured as sequential abatements, or the reporting is imprecise.',
        sourceIds: ['src_stat_469_1813', 'src_city_subsidy'],
      },
    ],
  },
  {
    id: 'agreement_term_years',
    label: 'Agreement term',
    plainLabel: 'years the deal runs',
    unit: 'ratio',
    value: 28,
    kind: 'fact',
    sigFigs: 2,
    asOf: '2026-01-20',
    sourceIds: ['src_city_subsidy'],
    confidence: 'documented',
  },
  {
    id: 'annual_net_local_tax',
    label: 'Net new local property tax in a typical year',
    plainLabel: 'net new taxes each year',
    unit: 'usd',
    value: { lo: 3_500_000, mid: 4_675_000, hi: 6_000_000 },
    scenarioValues: { not_built: 0 },
    kind: 'estimate',
    sigFigs: 2,
    asOf: '2026-01-20',
    sourceIds: ['src_city_subsidy', 'src_tool_methodology'],
    confidence: 'derived',
    note:
      'The city publishes a 28-year total but not the year-by-year schedule. The central figure is that total divided evenly across the term; the band reflects that real receipts start low and rise as each stage is completed and assessed.',
  },
  {
    id: 'conservation_fee_annual',
    label: 'Annual state energy conservation fee',
    plainLabel: 'yearly state fee',
    unit: 'usd',
    value: 5_000_000,
    scenarioValues: { not_built: 0 },
    kind: 'policy',
    sigFigs: 2,
    asOf: '2025-06-15',
    sourceIds: ['src_stat_216b72'],
    confidence: 'documented',
    note:
      'Minn. Stat. § 216B.72 charges large-scale data centers an annual fee by load: $2M at 100–250 MW rising to $5M above 750 MW. Almost no developer study includes it. The amount an individual facility actually pays is non-public by statute.',
  },

  // ---------------------------------------------------------- rates and bills
  //
  // Read the confidence flags here before using any of it. The benefit figure
  // is a company estimate in an SEC earnings release, describing an agreement
  // the Commission has not approved, produced by a model filed as a trade
  // secret. The denominators underneath it are solid federal data. That
  // asymmetry is the whole story of this section, and the sliders exist so a
  // reader can see how little of the answer the sourced part determines.

  {
    id: 'xcel_customer_benefit_total',
    label: 'Utility-estimated total benefit to Minnesota customers',
    plainLabel: 'what the utility says customers save in total',
    unit: 'usd',
    value: 1_100_000_000,
    scenarioValues: { not_built: 0 },
    kind: 'estimate',
    sigFigs: 2,
    asOf: '2026-07-30',
    sourceIds: ['src_xcel_8k_q2_2026', 'src_mpuc_esa_docket'],
    // Not `documented`: the sentence is documented, the number behind it is not
    // checkable. The workbook that produces it is a claimed trade secret.
    confidence: 'modeled',
    note:
      'One sentence in Xcel’s Q2 2026 earnings release: the agreement "is expected to result in approximately $1.1 billion of benefits to NSP-Minnesota’s customers". It states no time period, no present-value basis and no class split. The agreement itself is pending at the Commission with a decision expected in early 2027.',
    contested: [
      {
        claim:
          'The figure is widely repeated as $1.5 billion over 15 years to residential and small business customers.',
        counterClaim:
          'That version appears only in uncited trade coverage. The primary disclosure says $1.1 billion, to "NSP-Minnesota’s customers", with no term and no class breakdown. This tool uses the primary figure.',
        sourceIds: ['src_xcel_8k_q2_2026'],
      },
      {
        claim: 'A large new load puts downward pressure on everyone else’s rates.',
        counterClaim:
          'Only where the revenue it brings exceeds the cost of serving it. That is a real condition, not a formality, and the published empirical work does not settle it: two working papers released in the same week of June 2026 reached opposite signs on whether data centers have raised or lowered residential prices.',
        sourceIds: ['src_rate_literature', 'src_mpuc_large_load_tariff'],
      },
    ],
  },
  {
    id: 'benefit_term_years',
    label: 'Term over which the benefit is assumed to accrue',
    plainLabel: 'years the saving is spread over',
    unit: 'years',
    value: 15,
    kind: 'assumption',
    sigFigs: 2,
    asOf: '2026-05-15',
    sourceIds: ['src_mpuc_large_load_tariff'],
    confidence: 'modeled',
    note:
      'The disclosure states no term. Fifteen years is the minimum contract length the Commission set for the large-load class, which makes it the most defensible available choice, but it is our assumption and not the company’s statement.',
    adjustable: {
      min: 10,
      max: 25,
      step: 1,
      sliderLabel: 'Years the benefit is spread over',
      rationale:
        'Lower bound is shorter than any contract the tariff permits. Upper bound approaches the depreciable life of the generation contracted alongside the facility. The published figure supports no point inside this range over any other, which is the reason this is a slider rather than a constant.',
    },
  },
  {
    id: 'residential_revenue_share',
    label: 'Residential share of the benefit',
    plainLabel: 'how much of the saving reaches households',
    unit: 'ratio',
    value: { lo: 0.313, mid: 0.3973, hi: 0.3973 },
    kind: 'assumption',
    sigFigs: 3,
    asOf: '2024-12-31',
    sourceIds: ['src_eia_861', 'src_mpuc_large_load_tariff'],
    confidence: 'modeled',
    note:
      'Xcel’s petition allocates large-load revenue to classes on the test-year base revenue allocator, which is not public. The public proxy is the residential share of retail revenue, 39.7%. The low end is the residential share of kWh sold, 31.3%, which is what the allocation would be on an energy basis.',
    adjustable: {
      min: 0.25,
      max: 0.45,
      step: 0.005,
      sliderLabel: 'Residential share',
      rationale:
        'Bounded by the two publicly computable allocators — energy share at the bottom, revenue share at the top — with room either side, because the allocator actually used is filed non-public.',
    },
  },
  {
    id: 'nsp_mn_residential_customers',
    label: 'Residential customers, Xcel Minnesota',
    plainLabel: 'households on the bill',
    unit: 'customers',
    value: 1_230_057,
    kind: 'fact',
    sigFigs: 4,
    asOf: '2024-12-31',
    sourceIds: ['src_eia_861'],
    confidence: 'documented',
    note:
      'Minnesota jurisdiction only. The same utility serves North Dakota and South Dakota, which are excluded because the Minnesota Commission sets Minnesota retail rates.',
  },
  {
    id: 'nsp_mn_total_sales',
    label: 'Total retail electricity sales, Xcel Minnesota',
    plainLabel: 'all the power it sells in Minnesota',
    unit: 'mwh_per_year',
    value: 27_722_191,
    kind: 'fact',
    sigFigs: 4,
    asOf: '2024-12-31',
    sourceIds: ['src_eia_861'],
    confidence: 'documented',
    note:
      'All classes, Minnesota jurisdiction, 2024. Used as the denominator for the system-wide price effect. The data center’s own consumption is deliberately NOT added to it: the benefit accrues to the customers other than the data center, so they are the right denominator.',
  },
  {
    id: 'avg_residential_monthly_bill',
    label: 'Average residential monthly bill, Xcel Minnesota',
    plainLabel: 'a typical monthly power bill',
    unit: 'usd_per_customer_month',
    value: 99.82,
    kind: 'estimate',
    sigFigs: 4,
    asOf: '2024-12-31',
    sourceIds: ['src_eia_861'],
    confidence: 'derived',
    note:
      'Residential revenue divided by residential customers divided by twelve: $1,473,375,300 ÷ 1,230,057 ÷ 12. An average across the year, so it is lower than a January bill and higher than an April one.',
  },
  {
    id: 'months_per_year',
    label: 'Months in a year',
    plainLabel: 'months in a year',
    unit: 'months_per_year',
    value: 12,
    kind: 'policy',
    sigFigs: 2,
    asOf: '2026-08-07',
    sourceIds: ['src_tool_methodology'],
    confidence: 'documented',
    note:
      'A conversion constant. It carries real dimensions so that the unit checker can prove an annual figure divided by it is genuinely a monthly one.',
  },
  {
    id: 'mpuc_rate_increase_2yr',
    label: 'Rate increase approved in the 2024 Minnesota rate case',
    plainLabel: 'the rate rise the regulator just approved',
    unit: 'usd',
    value: 211_000_000,
    kind: 'fact',
    sigFigs: 3,
    asOf: '2026-06-30',
    sourceIds: ['src_xcel_8k_q2_2026'],
    confidence: 'documented',
    note:
      'Docket E-002/GR-24-320, decided by the Commission in June 2026: roughly $211 million over two years, an annual average increase of 2.9%. Shown alongside the benefit figure because it is the same utility, the same regulator and the same period, and it is what is actually moving bills.',
  },

  // ---------------------------------------------------------- jobs

  {
    id: 'construction_job_years_direct',
    label: 'Direct construction employment, Phase 1',
    plainLabel: 'construction work, Phase 1',
    unit: 'job_years',
    value: { lo: 640, mid: 700, hi: 755 },
    scenarioValues: { not_built: 0 },
    kind: 'estimate',
    sigFigs: 2,
    asOf: '2026-01-20',
    sourceIds: ['src_city_econ_impact'],
    confidence: 'derived',
    note:
      'These are JOB-YEARS over a two-year build, not simultaneous jobs. Spread over two years it is roughly 320 to 380 people on site in an average year. The band is the study’s own three local-purchase scenarios.',
    contested: [
      {
        claim:
          'Three different construction employment figures circulate: 700 (Extension), about 500 skilled trades (the developer), and “upwards of 1,000” (the city, if phases overlap).',
        counterClaim:
          'They measure three different things — job-years, average craft headcount, and peak headcount with overlapping stages. None is wrong; they are not comparable.',
        sourceIds: ['src_city_econ_impact', 'src_city_skyway'],
      },
    ],
  },
  {
    id: 'craft_hours_per_job_year',
    label: 'Craft hours per job-year',
    plainLabel: 'hours in a year of work',
    unit: 'hours_per_job_year',
    value: { lo: 1800, mid: 2000, hi: 2000 },
    kind: 'assumption',
    sigFigs: 2,
    asOf: '2026-08-04',
    sourceIds: ['src_tool_methodology'],
    confidence: 'modeled',
    adjustable: {
      min: 1600,
      max: 2080,
      step: 40,
      sliderLabel: 'Hours per job-year',
      rationale:
        'A full-time construction year is 2,080 hours at 40 hours a week. IMPLAN job-years are adjusted for part-time and seasonality, so 1,800 is the conservative end.',
    },
  },
  {
    id: 'apprentice_hour_share',
    label: 'Share of craft hours worked by registered apprentices',
    plainLabel: 'how much of the work apprentices do',
    unit: 'ratio',
    value: { lo: 0.15, mid: 0.175, hi: 0.2 },
    kind: 'assumption',
    sigFigs: 2,
    asOf: '2026-08-04',
    sourceIds: ['src_stat_178_036', 'src_stat_297a68', 'src_tool_methodology'],
    confidence: 'modeled',
    note:
      'Minnesota ratios are set by collective bargaining agreement first. Absent one, the building trades default asymptotes to one apprentice per three journeyworkers, or 25% of the crew. That is a legal ceiling, not an expectation. Prevailing wage does apply here — not because of the local tax abatement, but because Minn. Stat. § 297A.68 subd. 42(k) requires it of large-scale data centers claiming the sales tax exemption.',
    adjustable: {
      min: 0.05,
      max: 0.25,
      step: 0.01,
      sliderLabel: 'Apprentice share of hours',
      rationale:
        'Upper bound is the statutory ceiling under Minn. Stat. § 178.036. Lower bound reflects a build with limited signatory participation.',
    },
  },
  {
    id: 'ojt_hours_per_apprentice_year',
    label: 'On-the-job training hours per apprentice-year',
    plainLabel: 'hours an apprentice needs in a year',
    unit: 'hours_per_apprentice_year',
    value: { lo: 1200, mid: 1500, hi: 2000 },
    kind: 'assumption',
    sigFigs: 2,
    asOf: '2025-02-26',
    sourceIds: ['src_dli_apprentice', 'src_tool_methodology'],
    confidence: 'modeled',
    note:
      'Programs vary widely: an electrical apprenticeship runs 10,000 hours over five years, a laborer program roughly 4,000 in total. 1,500 is a conservative blend.',
    adjustable: {
      min: 1000,
      max: 2000,
      step: 100,
      sliderLabel: 'OJT hours per apprentice-year',
      rationale:
        'Bounded by the shortest and longest registered program structures in Minnesota building trades.',
    },
  },
  {
    id: 'permanent_jobs_full_buildout',
    label: 'Permanent operations jobs at full buildout',
    plainLabel: 'permanent jobs, eventually',
    unit: 'count',
    value: 290,
    scenarioValues: { not_built: 0 },
    kind: 'fact',
    sigFigs: 2,
    asOf: '2026-01-20',
    sourceIds: ['src_city_skyway'],
    confidence: 'documented',
    note:
      'Across nine stages over a 15 to 20 year buildout: 38 jobs at stage one, declining by two per stage to a floor of 30.',
  },

  // ================================================================ civic

  {
    id: 'panther_base_payment',
    label: 'Panther Program payment to the school district, first year',
    plainLabel: 'school payment, first year',
    unit: 'usd',
    value: 500_000,
    scenarioValues: { not_built: 0 },
    kind: 'fact',
    sigFigs: 3,
    asOf: '2026-01-20',
    sourceIds: ['src_city_subsidy', 'src_isd255_budget'],
    confidence: 'documented',
    note:
      'A payment from the developer, negotiated separately from the tax mechanics. It is genuinely new money to the district, which most tax-driven school revenue is not. For scale, the district’s general fund expenditure is about $19.7 million a year against 1,524 pupils.',
  },
  {
    id: 'panther_escalator',
    label: 'Annual increase in the Panther Program payment',
    plainLabel: 'how much the school payment grows each year',
    unit: 'usd',
    value: 25_000,
    scenarioValues: { not_built: 0 },
    kind: 'fact',
    sigFigs: 2,
    asOf: '2026-01-20',
    sourceIds: ['src_city_subsidy'],
    confidence: 'documented',
  },
  {
    id: 'teacher_avg_salary',
    label: 'Average teacher salary, Pine Island ISD 255, 2025-26',
    plainLabel: 'what a local teacher earns',
    unit: 'usd_per_teacher_year',
    value: 66_159,
    kind: 'fact',
    sigFigs: 3,
    asOf: '2026-06-30',
    sourceIds: ['src_pelsb_salary'],
    confidence: 'documented',
    note:
      'The district’s own figure, not a state average — the statewide average of $77,655 is pulled up by metro districts. Contract salary only, which is why a benefit load is applied separately.',
    contested: [
      {
        claim: 'The district’s reported average rose 13.5% year over year, from $58,286 to $66,159.',
        counterClaim:
          'That is a large jump. It is plausibly a new master agreement, but it could be a reporting artefact and has not been confirmed with the district.',
        sourceIds: ['src_pelsb_salary'],
      },
    ],
  },
  {
    id: 'teacher_benefit_load',
    label: 'Employer benefit load on teacher salary',
    plainLabel: 'benefits on top of salary',
    unit: 'ratio',
    value: { lo: 1.33, mid: 1.39, hi: 1.5 },
    kind: 'assumption',
    sigFigs: 3,
    asOf: '2026-08-04',
    sourceIds: ['src_tra_rates', 'src_tool_methodology'],
    confidence: 'derived',
    note:
      'Built from TRA employer contribution 9.81%, FICA and Medicare 7.65%, employer health insurance around $13,000 a year, and roughly 2.3% for life, disability, dental, workers compensation and unemployment.',
    adjustable: {
      min: 1.25,
      max: 1.6,
      step: 0.01,
      sliderLabel: 'Benefit load multiplier',
      rationale:
        'The band is driven almost entirely by the employer health contribution, which ranges from $9,200 to $19,332 across current Minnesota small-district agreements. Pine Island reported a 19% premium increase for 2026, which pushes toward the upper end.',
    },
  },
  {
    id: 'city_general_fund',
    label: 'City of Pine Island general fund, 2026',
    plainLabel: 'the city’s yearly budget',
    unit: 'usd',
    value: 5_100_664,
    kind: 'fact',
    sigFigs: 4,
    asOf: '2025-12-16',
    sourceIds: ['src_city_tnt_2026'],
    confidence: 'documented',
    note: 'Property taxes fund about 70% of it.',
  },
  {
    id: 'city_fire_budget',
    label: 'City fire budget, 2026',
    plainLabel: 'the fire department’s budget',
    unit: 'usd',
    value: 549_045,
    kind: 'fact',
    sigFigs: 4,
    asOf: '2025-12-16',
    sourceIds: ['src_city_tnt_2026', 'src_city_fire'],
    confidence: 'documented',
    note:
      'An all-volunteer department covering roughly 130 square miles and about 10,100 people across the city and five townships.',
  },
  {
    id: 'city_police_budget',
    label: 'City policing budget, 2026',
    plainLabel: 'the policing budget',
    unit: 'usd',
    value: 522_850,
    kind: 'fact',
    sigFigs: 4,
    asOf: '2025-12-16',
    sourceIds: ['src_city_tnt_2026', 'src_goodhue_budget'],
    confidence: 'documented',
    note:
      'Contracted to the Goodhue County Sheriff at 126 hours a week. The 2026 cost rose 12% on the county’s hourly rate alone, with no added hours.',
  },
  {
    id: 'betterment_annual_min',
    label: 'Community Betterment Fund, minimum first-year payment',
    plainLabel: 'community fund, first year',
    unit: 'usd',
    value: 50_000,
    scenarioValues: { not_built: 0 },
    kind: 'fact',
    sigFigs: 2,
    asOf: '2026-01-20',
    sourceIds: ['src_city_subsidy'],
    confidence: 'documented',
  },
  {
    id: 'betterment_escalator',
    label: 'Annual increase in the Community Betterment Fund',
    plainLabel: 'how much the community fund grows',
    unit: 'usd',
    value: 5_000,
    scenarioValues: { not_built: 0 },
    kind: 'fact',
    sigFigs: 2,
    asOf: '2026-01-20',
    sourceIds: ['src_city_subsidy'],
    confidence: 'documented',
  },

  // ================================================================ social — emissions

  {
    id: 'datacenter_load_mw',
    label: 'Data center electrical load at full buildout',
    plainLabel: 'how much power it draws',
    unit: 'mw',
    value: { lo: 700, mid: 1000, hi: 1500 },
    kind: 'assumption',
    sigFigs: 2,
    asOf: '2026-08-04',
    sourceIds: ['src_city_skyway', 'src_google_blog', 'src_tool_methodology'],
    confidence: 'modeled',
    note:
      'Neither Google nor Xcel has published a load figure. 1,000 MW across roughly 3 million square feet is about 333 watts per square foot, which is conservative for an AI campus and well below the 2,700 MW ceiling opponents cite from the environmental review.',
    adjustable: {
      min: 300,
      max: 2700,
      step: 50,
      sliderLabel: 'Facility load (MW)',
      rationale:
        'Lower bound is a single-stage facility. Upper bound is the full-buildout ceiling referenced in the AUAR scenario documents by the project’s opponents.',
    },
  },
  {
    id: 'load_factor',
    label: 'Load factor',
    plainLabel: 'how steadily it runs',
    unit: 'ratio',
    value: { lo: 0.78, mid: 0.85, hi: 0.92 },
    kind: 'assumption',
    sigFigs: 2,
    asOf: '2026-08-04',
    sourceIds: ['src_tool_methodology'],
    confidence: 'modeled',
    note:
      'Hyperscale facilities typically run at 80 to 95% of contracted capacity. Xcel’s large load tariff sets a minimum billing demand of 75%, which is a regulatory floor rather than a forecast.',
    adjustable: {
      min: 0.6,
      max: 0.98,
      step: 0.01,
      sliderLabel: 'Load factor',
      rationale:
        'Bounded below by the tariff minimum billing demand and above by the practical ceiling for a facility that must reserve headroom for maintenance.',
    },
  },
  {
    id: 'hours_per_year',
    label: 'Hours in a year',
    plainLabel: 'hours in a year',
    unit: 'hours_per_year',
    value: 8760,
    kind: 'policy',
    sigFigs: 4,
    asOf: '2026-08-04',
    sourceIds: ['src_tool_methodology'],
    confidence: 'documented',
  },
  {
    id: 'tons_per_lb',
    label: 'Short tons per pound',
    plainLabel: 'pounds to tons',
    unit: 'short_tons_per_lb',
    value: 0.0005,
    kind: 'policy',
    sigFigs: 4,
    asOf: '2026-08-04',
    sourceIds: ['src_tool_methodology'],
    confidence: 'documented',
  },
  {
    id: 'serving_grid_co2e',
    label: 'Carbon intensity of the grid actually serving this load',
    plainLabel: 'carbon of the grid that powers it',
    unit: 'lb_per_mwh',
    value: 926.6,
    // In the "built somewhere else" scenario the load IS served on the Missouri
    // grid, so nothing is avoided and the metric correctly falls to zero.
    scenarioValues: { not_built: 1248.6 },
    kind: 'fact',
    sigFigs: 4,
    asOf: '2025-06-12',
    sourceIds: ['src_egrid'],
    confidence: 'documented',
    note:
      'Minnesota sits in eGRID subregion MROW, which is 35.2% coal and 37.6% wind. If the load is served in Missouri instead, this becomes the SRMW rate and there is nothing to avoid.',
  },
  {
    id: 'alt_grid_co2e',
    label: 'Comparison grid carbon intensity (eGRID subregion SRMW, Missouri)',
    plainLabel: 'Missouri grid, carbon',
    unit: 'lb_per_mwh',
    value: 1248.6,
    kind: 'fact',
    sigFigs: 4,
    asOf: '2025-06-12',
    sourceIds: ['src_egrid'],
    confidence: 'documented',
    note:
      'SRMW is 51.1% coal. Missouri is the comparison because Google announced a $15 billion data center there in May 2026, so it is a real alternative rather than a hypothetical one.',
    contested: [
      {
        claim:
          'Comparing total output rates shows a 322 lb/MWh gap between the two grids.',
        counterClaim:
          'eGRID also publishes non-baseload rates, intended as the proxy for incremental load. On those the carbon gap nearly closes, 1,788.6 against 1,831.1. The sulphur dioxide gap holds up on either basis.',
        sourceIds: ['src_egrid'],
      },
    ],
  },
  {
    id: 'serving_grid_so2',
    label: 'Sulphur dioxide intensity of the grid actually serving this load',
    plainLabel: 'sulphur of the grid that powers it',
    unit: 'lb_per_mwh',
    value: 0.896,
    scenarioValues: { not_built: 1.636 },
    kind: 'fact',
    sigFigs: 3,
    asOf: '2025-06-12',
    sourceIds: ['src_egrid'],
    confidence: 'documented',
    note:
      'This understates how clean Minnesota itself is: the MROW subregion figure is raised by North Dakota lignite. Minnesota’s own state rate is 0.187.',
  },
  {
    id: 'alt_grid_so2',
    label: 'Comparison grid sulphur dioxide intensity (SRMW)',
    plainLabel: 'Missouri grid, sulphur dioxide',
    unit: 'lb_per_mwh',
    value: 1.636,
    kind: 'fact',
    sigFigs: 4,
    asOf: '2025-06-12',
    sourceIds: ['src_egrid'],
    confidence: 'documented',
  },
  {
    id: 'contracted_new_generation_mw',
    label: 'New generation and storage contracted for this load',
    plainLabel: 'new clean power built for it',
    unit: 'mw',
    value: 1900,
    scenarioValues: { not_built: 0 },
    kind: 'fact',
    sigFigs: 2,
    asOf: '2026-02-24',
    sourceIds: ['src_xcel_release'],
    confidence: 'documented',
    note:
      '1,400 MW wind, 200 MW solar and 300 MW of Form Energy iron-air storage rated at 30 GWh, roughly 100 hours. This is nameplate capacity, not the facility’s load, and the two are routinely confused.',
  },
  {
    id: 'accredited_capacity_mw',
    label: 'Accredited capacity of the contracted portfolio, summer',
    plainLabel: 'how much of that counts when the grid is tightest',
    unit: 'mw',
    value: { lo: 374, mid: 500, hi: 700 },
    scenarioValues: { not_built: 0 },
    kind: 'assumption',
    sigFigs: 2,
    asOf: '2026-08-04',
    sourceIds: ['src_miso_dlol', 'src_miso_wind_solar', 'src_tool_methodology'],
    confidence: 'modeled',
    note:
      'Nameplate is not capacity. Under MISO’s DLOL method, which takes effect for the 2028-29 planning year — exactly when these resources energise — 1,400 MW of wind accredits at about 8% in summer and 200 MW of solar at 38%. The largest single uncertainty is the 100-hour storage, for which MISO has published nothing; its own results state they assume 4-hour batteries.',
    adjustable: {
      min: 350,
      max: 900,
      step: 25,
      sliderLabel: 'Accredited capacity (MW)',
      rationale:
        'Lower bound applies DLOL with storage accredited as a 4-hour battery. Upper bound applies the outgoing seasonal construct with storage accredited near-firm. Values above roughly 700 MW are not supported by any published MISO methodology.',
    },
  },

  // ================================================================ social — water

  {
    id: 'water_permitted_mgy',
    label: 'Permitted maximum water use',
    plainLabel: 'most water it could use',
    unit: 'million_gallons_per_year',
    value: 55.5,
    scenarioValues: { not_built: 0 },
    kind: 'fact',
    sigFigs: 3,
    asOf: '2026-01-20',
    sourceIds: ['src_city_skyway'],
    confidence: 'documented',
    note:
      '152,000 gallons a day at 365 days. Domestic use only — the facility is air-cooled and closed-loop, so there is no cooling tower draw. This is a permitted ceiling at a site that has withdrawn nothing yet, not a measurement.',
    contested: [
      {
        claim: 'Opponents cite 55.7 million gallons a year and a roughly 50% increase in the town’s water consumption.',
        counterClaim:
          'That is the same number, annualised slightly differently. The volume is not actually in dispute; the dispute is whether the environmental review adequately studied aquifer and private well impacts.',
        sourceIds: ['src_city_skyway'],
      },
    ],
  },
  {
    id: 'golf_pine_island_permitted_mgy',
    label: 'Pine Island Golf Course, permitted maximum',
    plainLabel: 'the golf course in town',
    unit: 'million_gallons_per_year',
    value: 44.0,
    kind: 'fact',
    sigFigs: 3,
    asOf: '2025-09-16',
    sourceIds: ['src_dnr_water'],
    confidence: 'documented',
    note: 'DNR permit 2004-4135. Actual reported withdrawal in 2024 was 9.4 million gallons.',
  },
  {
    id: 'golf_median_permitted_mgy',
    label: 'Median Minnesota golf course, permitted maximum',
    plainLabel: 'a typical Minnesota golf course',
    unit: 'million_gallons_per_year',
    value: 26.0,
    kind: 'estimate',
    sigFigs: 3,
    asOf: '2025-09-16',
    sourceIds: ['src_dnr_water'],
    confidence: 'derived',
    note: 'Median across 402 active golf irrigation permits. Median actual withdrawal in 2024 was 9.9 million gallons.',
  },
  {
    id: 'city_water_actual_mgy',
    label: 'City of Pine Island municipal system, actual 2024 withdrawal',
    plainLabel: 'what the whole town actually pumped',
    unit: 'million_gallons_per_year',
    value: 113.3,
    kind: 'fact',
    sigFigs: 4,
    asOf: '2024-12-31',
    sourceIds: ['src_dnr_water'],
    confidence: 'documented',
    note: 'DNR permit 1981-5157. The city is permitted for 166 million gallons a year.',
  },
  {
    id: 'irrigation_mgy_per_acre',
    label: 'Crop irrigation in a dry southeast Minnesota season',
    plainLabel: 'watering one acre of crops',
    unit: 'million_gallons_per_year',
    value: 0.179,
    kind: 'estimate',
    sigFigs: 3,
    asOf: '2023-12-31',
    sourceIds: ['src_dnr_water'],
    confidence: 'derived',
    note:
      'Median across 579 active crop irrigation permits in the ten southeast counties in 2023, a dry year. About 6.6 acre-inches, which is right for supplemental irrigation here. In the wet year of 2024 the median was less than half this.',
  },
  {
    id: 'ski_snowmaking_actual_mgy',
    label: 'Welch Village ski area snowmaking, actual 2024',
    plainLabel: 'the ski hill down the road',
    unit: 'million_gallons_per_year',
    value: 62.0,
    kind: 'fact',
    sigFigs: 3,
    asOf: '2024-12-31',
    sourceIds: ['src_dnr_water'],
    confidence: 'documented',
    note:
      'Two permits in Goodhue County, the closest single match in the county. Snowmaking water largely returns to the local watershed as melt, so the consumptive fraction differs.',
  },

  // ================================================================ social — noise

  {
    id: 'noise_night_estimate_dba',
    label: 'Estimated night-time noise at the nearest residence',
    plainLabel: 'how loud at night',
    unit: 'dba',
    value: 49,
    scenarioValues: { not_built: 0 },
    kind: 'fact',
    sigFigs: 2,
    asOf: '2026-01-20',
    sourceIds: ['src_city_noise', 'src_city_skyway'],
    confidence: 'documented',
  },
  {
    id: 'noise_night_l50_limit_dba',
    label: 'Minnesota night-time residential limit, L50',
    plainLabel: 'the night-time limit that applies',
    unit: 'dba',
    value: 50,
    kind: 'policy',
    sigFigs: 2,
    asOf: '2026-08-04',
    sourceIds: ['src_mn_rule_7030', 'src_mpca_noise'],
    confidence: 'documented',
    note:
      'L50 is the level exceeded for half of any hour. A facility that runs continuously is an L50 source by definition, so this is the binding standard — not the L10 figure of 55 dBA that the project’s public materials compare against.',
  },
  {
    id: 'rural_night_ambient_dba',
    label: 'Rural night-time background noise',
    plainLabel: 'how quiet it is now at night',
    unit: 'dba',
    value: { lo: 30, mid: 35, hi: 40 },
    kind: 'estimate',
    sigFigs: 2,
    asOf: '2026-08-04',
    sourceIds: ['src_mpca_noise'],
    confidence: 'derived',
    note: 'A 10 dB increase is generally perceived as roughly a doubling of loudness.',
  },
];
